import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useParams } from 'react-router-dom'
import { api, type SubcontractorProfile, type SubContact } from '../lib/api'
import { ProfileView } from '../components/ProfileView'
import { RangePicker, type RangeValue } from '../components/RangePicker'
import { PhoneLink, EmailLink } from '../components/links'
import { formatMoney } from '../lib/format'

export function SubcontractorProfilePage() {
  const { id = '' } = useParams()
  const [range, setRange] = useState<RangeValue>({ range: 'year', from: '', to: '' })
  const [data, setData] = useState<SubcontractorProfile | null>(null)
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading')

  const load = useCallback(async () => {
    try {
      const d = await api.getSubcontractorProfile(id, range.range, range.from, range.to)
      setData(d)
      setState('ok')
    } catch {
      setState('error')
    }
  }, [id, range])

  useEffect(() => {
    void load()
  }, [load])

  if (state === 'loading' && !data) return <div style={{ color: 'var(--gray)' }}>Loading…</div>
  if (state === 'error' || !data) return <div className="page-title">Subcontractor not found</div>

  const s = data.subcontractor
  return (
    <ProfileView
      backTo="/app/subcontractors"
      backLabel="Back to subcontractors"
      title={s.name}
      subtitle={s.specialty ? `Subcontractor · ${s.specialty}` : 'Subcontractor'}
      filter={<RangePicker value={range} onChange={setRange} />}
      contact={[]}
      contactEditor={<ContactsManager subId={id} contacts={data.contacts} onChange={load} />}
      stats={[
        { label: 'Total cases', value: String(data.stats.total) },
        { label: 'Open', value: String(data.stats.open), accent: 'var(--status-open)' },
        { label: 'In Progress', value: String(data.stats.in_progress), accent: 'var(--status-in-progress)' },
        { label: 'Closed', value: String(data.stats.closed), accent: 'var(--status-closed)' },
        { label: 'Repair cost', value: formatMoney(data.stats.totalCost) },
      ]}
      cases={data.cases}
    />
  )
}

function ContactsManager({
  subId,
  contacts,
  onChange,
}: {
  subId: string
  contacts: SubContact[]
  onChange: () => void | Promise<void>
}) {
  const [adding, setAdding] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)

  async function setMain(cid: string) {
    await api.updateSubContact(subId, cid, { is_main: true })
    await onChange()
  }
  async function remove(c: SubContact) {
    if (!window.confirm(`Remove contact ${c.name}?`)) return
    await api.deleteSubContact(subId, c.id)
    await onChange()
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontWeight: 700 }}>Contacts</div>
        {!adding && !editId && (
          <button className="link-btn" style={{ marginLeft: 'auto' }} onClick={() => setAdding(true)}>
            + Add contact
          </button>
        )}
      </div>

      {contacts.length === 0 && !adding && (
        <p style={{ fontSize: 13, color: 'var(--gray)' }}>No contacts yet.</p>
      )}

      <div style={{ display: 'grid', gap: 10 }}>
        {contacts.map((c) =>
          editId === c.id ? (
            <ContactForm
              key={c.id}
              subId={subId}
              contact={c}
              onDone={async () => {
                setEditId(null)
                await onChange()
              }}
              onCancel={() => setEditId(null)}
            />
          ) : (
            <div
              key={c.id}
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 8,
                padding: '10px 0',
                borderTop: '1px solid var(--line)',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>
                  {c.name}
                  {c.title && <span style={{ color: 'var(--gray)', fontWeight: 400 }}> · {c.title}</span>}
                  {c.is_main === 1 && (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 11,
                        fontWeight: 700,
                        color: 'var(--azure)',
                        border: '1px solid var(--azure)',
                        borderRadius: 4,
                        padding: '1px 6px',
                      }}
                    >
                      MAIN
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 13, color: 'var(--gray)', display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  {c.phone && <PhoneLink phone={c.phone} />}
                  {c.email && <EmailLink email={c.email} />}
                  {!c.phone && !c.email && '—'}
                </div>
              </div>
              <div style={{ whiteSpace: 'nowrap' }}>
                {c.is_main !== 1 && (
                  <button className="link-btn" onClick={() => void setMain(c.id)}>Make main</button>
                )}
                <button className="link-btn" style={{ marginLeft: 12 }} onClick={() => setEditId(c.id)}>Edit</button>
                <button className="link-btn" style={{ color: 'var(--red)', marginLeft: 12 }} onClick={() => void remove(c)}>Remove</button>
              </div>
            </div>
          ),
        )}
      </div>

      {adding && (
        <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12, marginTop: 4 }}>
          <ContactForm
            subId={subId}
            contact={null}
            onDone={async () => {
              setAdding(false)
              await onChange()
            }}
            onCancel={() => setAdding(false)}
          />
        </div>
      )}
    </div>
  )
}

function ContactForm({
  subId,
  contact,
  onDone,
  onCancel,
}: {
  subId: string
  contact: SubContact | null
  onDone: () => void | Promise<void>
  onCancel: () => void
}) {
  const [name, setName] = useState(contact?.name ?? '')
  const [title, setTitle] = useState(contact?.title ?? '')
  const [phone, setPhone] = useState(contact?.phone ?? '')
  const [email, setEmail] = useState(contact?.email ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!name.trim()) {
      setError('Name is required.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const payload = { name: name.trim(), title, phone, email }
      if (contact) await api.updateSubContact(subId, contact.id, payload)
      else await api.addSubContact(subId, payload)
      await onDone()
    } catch {
      setError('Could not save. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={onSubmit}>
      <div className="edit-grid">
        <div className="field" style={{ margin: 0 }}><label>Name *</label><input value={name} onChange={(e) => setName(e.target.value)} required /></div>
        <div className="field" style={{ margin: 0 }}><label>Title</label><input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Owner" /></div>
        <div className="field" style={{ margin: 0 }}><label>Phone</label><input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
        <div className="field" style={{ margin: 0 }}><label>Email</label><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
      </div>
      {error && <p className="form-error">{error}</p>}
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button className="btn btn-primary" type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
        <button type="button" className="btn" style={{ border: '1px solid var(--line)' }} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  )
}
